@file:OptIn(ExperimentalResourceApi::class, ExperimentalResourceApi::class)

package ui.widget

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.*
import androidx.compose.foundation.shape.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.draw.*
import androidx.compose.ui.geometry.*
import androidx.compose.ui.graphics.*
import androidx.compose.ui.graphics.vector.*
import androidx.compose.ui.layout.*
import androidx.compose.ui.platform.*
import androidx.compose.ui.text.font.*
import androidx.compose.ui.text.style.*
import androidx.compose.ui.unit.*
import org.jetbrains.compose.resources.*
import tools.*
import kotlin.math.*


fun Modifier.tableMeasurer(measurer: ListingSpecs.Measurer): Modifier = this then onGloballyPositioned {
    measurer.onGloballyPositioned(it)
}

class ListingSpecs<T> private constructor(val items: List<Item<T>>) {

    private fun calculateWidth(spacing: Float = 16F, padding: Float = 16F): Float {
        return (items.sumOf { it.width.value.toDouble() } + (padding * 2.0) + ((items.size) * spacing)).toFloat()
    }


    @Composable
    fun rememberMeasurer(spacing: Dp = 16.dp, padding: Dp = 8.dp): Measurer {
        val density = LocalDensity.current
        return remember(spacing, padding) {
            Measurer(
                calculateWidth(spacing.value, padding.value),
                density,
            )
        }
    }

    class Measurer(
        private val initialSize: Float,
        private val density: Density
    ) {

        var rowWidth by mutableStateOf(initialSize.dp)
            private set

        var contentBoundary by mutableStateOf<Rect?>(null)
            private set

        fun onGloballyPositioned(layoutCoordinates: LayoutCoordinates) {
            contentBoundary = layoutCoordinates.boundsInWindow() / density.density
            rowWidth = minOf(1920F, maxOf(layoutCoordinates.boundsInParent().size.width / density.density, initialSize)).dp
        }
    }

    class Item<T>(
        val title: StringResource,
        val width: Dp,
        val lines: Int = 2,
        val align: TextAlign = TextAlign.Left,
        val divider: Dp = 16.dp,
        var body: @Composable (T) -> Any
    )

    companion object {
        fun <T> create(vararg items: Item<T>): ListingSpecs<T> {
            return ListingSpecs(items.toList())
        }

        val trimmedId: @Composable (Long) -> Any = {
            it.toString()
                .dropLast(3)
                .toLongOrNull()
                ?.toString(16)
                ?.uppercase()
                ?: it
        }
    }
}

operator fun Rect.div(density: Float): Rect = Rect(
    left = left / density,
    top = top / density,
    right = right / density,
    bottom = bottom / density,
)

@Composable
fun <T> LazyItemScope.ListingHeader(specs: ListingSpecs<T>, modifier: Modifier = Modifier) {
    Row(
        modifier.fillMaxWidth()
            .clip(RoundedCornerShape(topStart = 8.dp, topEnd = 8.dp, 0.dp, 0.dp))
            .background(MaterialTheme.colorScheme.surfaceContainer)
            .padding(16.dp)
            .padding(horizontal = 8.dp)
    ) {
        specs.items.forEachIndexed { index, item ->
            if (index != 0) Space(item.divider)
            Text(
                text = item.title.rememberString(),
                textAlign = item.align,
                modifier = Modifier.width(item.width)
                    .align(Alignment.CenterVertically),
                maxLines = item.lines,
                overflow = TextOverflow.Ellipsis,
                fontWeight = FontWeight.ExtraLight,
                style = MaterialTheme.typography.titleSmall
            )
        }
    }
}

sealed interface ListingItemBody {
    data class Icon(val icon: ImageVector, val tint: Color = Color.White) : ListingItemBody
    data class Progress(val pct: Float) : ListingItemBody
    data class Composable(val content: @androidx.compose.runtime.Composable (Modifier) -> Unit) : ListingItemBody
}


fun LazyListScope.ListingFooter() {
    item {
        Spacer(
            Modifier
                .padding(bottom = 64.dp)
                .fillMaxWidth()
                .height(8.dp)
                .background(MaterialTheme.colorScheme.surfaceContainer, RoundedCornerShape(0.dp, 0.dp, 8.dp, 8.dp))
        )
    }
}

@Composable
fun LazyItemScope.ListingDivider() {
    HorizontalDivider(
        Modifier
            .background(MaterialTheme.colorScheme.surfaceContainer)
            .padding(horizontal = 16.dp),
        thickness = .5.dp
    )
}

@Composable
fun <T> LazyItemScope.ListingItem(
    item: T,
    specs: ListingSpecs<T>,
    background: Color = MaterialTheme.colorScheme.surfaceContainer,
    space: Dp = 16.dp,
    onClick: (T) -> Unit
) {
    // .defaultMinSize(minWidth = (specs.items.sumOf { it.width.value.toDouble() } +(specs.items.size-1 * space.value) ).dp)
    Row(Modifier.fillMaxWidth()
        .background(background)
        .clickable { onClick(item) }
        .padding(horizontal = 16.dp)
        .padding(8.dp),
        horizontalArrangement = Arrangement.spacedBy(space)) {
        specs.items.forEachIndexed { _, entry ->
            when (val data = entry.body(item)) {
                is ListingItemBody -> when (data) {
                    is ListingItemBody.Icon -> {
                        Box(Modifier.width(entry.width).align(Alignment.CenterVertically)) {
                            Icon(
                                imageVector = data.icon,
                                tint = data.tint,
                                contentDescription = null,
                                modifier = Modifier.align(Alignment.Center)
                            )
                        }
                    }

                    is ListingItemBody.Progress -> {
                        Column(Modifier.width(entry.width)) {
                            val pctText = if (data.pct > 1F) {
                                "100+%"
                            } else {
                                "${(data.pct.coerceIn(0F, 1F) * 100F).roundToInt()}%"
                            }
                            Text(
                                text = pctText,
                                modifier = Modifier.align(Alignment.CenterHorizontally)
                            )
                            Space(4.dp)
                            LinearProgressIndicator({ data.pct.coerceIn(0F, 1F) }, Modifier.fillMaxWidth())
                        }
                    }

                    is ListingItemBody.Composable -> Box(Modifier.width(entry.width).align(Alignment.CenterVertically)) {
                        data.content(Modifier.align(Alignment.Center))
                    }
                }

                else -> Text(
                    text = data.toString(),
                    textAlign = entry.align,
                    style = MaterialTheme.typography.bodyMedium,
                    maxLines = entry.lines,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.width(entry.width).align(Alignment.CenterVertically)
                )
            }

        }
    }
}
